/**
 * The CCS Approach — homepage.
 * Full banner atmosphere: deep brown-black richness, molten-gold curved
 * accents, cream typography. Arrival hierarchy: animated logo →
 * THE CCS APPROACH → COMMUNICATE • COLLABORATE • SERVE → mission →
 * six navigation cards → latest previews → testimonials → final CTA.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import IntroAnimation from "@/components/home/IntroAnimation";
import GoldCurve from "@/components/home/GoldCurve";
import HomeBelowFold from "@/components/home/HomeBelowFold";
import { EmblemImage } from "@/components/brand/Logo";

const EASE = [0.22, 1, 0.36, 1];
const INTRO_KEY = "ccs_intro_seen";
const HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_ccs-consult/artifacts/k0vyubd6_image.png";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const NAV_LINKS = [
  { label: "About", to: "/book#about" },
  { label: "Framework", to: "/book#philosophy" },
  { label: "Services", to: "/book#services" },
  { label: "Spotlight", to: "/spotlight" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/book#booking" },
];

const CARDS = [
  {
    to: "/spotlight", testid: "nav-card-spotlight", eyebrow: "Celebrating excellence",
    title: "CCS Spotlight", sub: "Real stories of people who communicate, collaborate and serve with excellence.",
  },
  {
    to: "/book#philosophy", testid: "nav-card-framework", eyebrow: "The philosophy",
    title: "CCS Framework", sub: "Explore the three pillars behind everything we do.",
  },
  {
    to: "/insights", testid: "nav-card-insights", eyebrow: "Thoughts worth pausing for",
    title: "CCS Insights", sub: "Reflections on leading, serving and working with intention.",
  },
  {
    to: "/book#services", testid: "nav-card-services", eyebrow: "How we help",
    title: "Services", sub: "Training and facilitation across nine development areas.",
  },
  {
    to: "/book#about", testid: "nav-card-joylance", eyebrow: "The founder",
    title: "Meet Joylance", sub: "The facilitator and heart behind The CCS Approach.", photo: true,
  },
  {
    to: "/book#booking", testid: "nav-card-book", eyebrow: "Let's talk",
    title: "Book a Consultation", sub: "A free discovery conversation about your team.", gold: true,
  },
];

const NavCard = ({ c }) => (
  <motion.div variants={item}>
    <Link
      to={c.to}
      data-testid={c.testid}
      aria-label={c.title}
      className={`group relative block h-full rounded-2xl p-7 sm:p-8 overflow-hidden transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E7B54D] focus-visible:ring-offset-4 focus-visible:ring-offset-[#17100B] ${
        c.gold
          ? "bg-gradient-to-b from-[#E7B54D] to-[#BB8327] hover:from-[#F5D88A] hover:to-[#C99135] shadow-[0_10px_40px_rgba(194,138,46,0.2)]"
          : "bg-[#221709] border border-[#C28A2E]/20 hover:border-[#C28A2E]/70 hover:shadow-[0_10px_40px_rgba(194,138,46,0.12)]"
      }`}
    >
      {!c.gold && (
        <div className="absolute -right-12 -top-16 w-44 h-44 rounded-full border border-[#C28A2E]/15 transition-colors duration-500 group-hover:border-[#C28A2E]/30" />
      )}
      {c.photo && (
        <img
          src={HEADSHOT_URL}
          alt="Joylance Chapman-George"
          className="w-14 h-14 rounded-full object-cover object-top ring-2 ring-[#C28A2E]/50 mb-4"
          data-testid="joylance-card-photo"
        />
      )}
      <div className={`text-[10px] uppercase tracking-[0.28em] font-bold ${c.gold ? "text-[#4A2E18]" : "text-[#E7B54D]"}`}>
        {c.eyebrow}
      </div>
      <h3 className={`mt-3 font-display text-xl sm:text-2xl font-medium transition-transform duration-300 group-hover:-translate-y-0.5 ${c.gold ? "text-[#2A1810]" : "text-[#FDFBF5]"}`}>
        {c.title}
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${c.gold ? "text-[#4A2E18]/85" : "text-[#F0E6D2]/60"}`}>{c.sub}</p>
      <span className={`mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] ${c.gold ? "text-[#2A1810]" : "text-[#E7B54D]"}`}>
        Enter
        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  </motion.div>
);

export default function HomePage() {
  const [showIntro, setShowIntro] = useState(() => {
    try {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return !reduced && !sessionStorage.getItem(INTRO_KEY);
    } catch {
      return true;
    }
  });

  const finishIntro = () => {
    try {
      sessionStorage.setItem(INTRO_KEY, "1");
    } catch {
      /* private mode */
    }
    setShowIntro(false);
  };

  return (
    <div className="min-h-screen bg-[#17100B] relative overflow-x-hidden" data-testid="home-gateway">
      <AnimatePresence>{showIntro && <IntroAnimation onDone={finishIntro} />}</AnimatePresence>

      <motion.div initial="hidden" animate={showIntro ? "hidden" : "show"} variants={container}>
        {/* ============== Slim top nav ============== */}
        <motion.header
          variants={item}
          className="relative z-20 max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between"
          data-testid="home-nav"
        >
          <div className="flex items-center gap-2.5">
            <EmblemImage className="w-9 h-9" testid="home-nav-logo" />
            <span className="font-display text-sm font-medium text-[#FDFBF5] tracking-wide hidden sm:inline">The CCS Approach</span>
          </div>
          <nav className="flex items-center gap-5 lg:gap-7">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="hidden md:inline text-xs uppercase tracking-[0.14em] text-[#F0E6D2]/60 hover:text-[#E7B54D] font-semibold transition-colors duration-300"
                data-testid={`home-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="text-xs uppercase tracking-[0.14em] text-[#F0E6D2]/40 hover:text-[#E7B54D] font-semibold transition-colors duration-300"
              data-testid="home-signin-link"
            >
              Sign in
            </Link>
          </nav>
        </motion.header>

        {/* ============== Hero — the brand statement ============== */}
        <section className="relative min-h-[92vh] flex flex-col items-center justify-center px-6 text-center overflow-hidden">
          <GoldCurve offsetY={-120} />
          <GoldCurve flip offsetY={230} className="opacity-70" />

          <div className="relative z-10 flex flex-col items-center max-w-4xl">
            {/* 1 — the emblem */}
            <motion.div variants={item} className="drop-shadow-[0_8px_30px_rgba(194,138,46,0.35)]">
              <EmblemImage className="w-24 h-24 sm:w-28 sm:h-28" testid="gateway-logo" />
            </motion.div>

            {/* 2 — the name: primary focal point */}
            <motion.h1
              variants={item}
              className="mt-10 font-display text-[2.6rem] leading-none sm:text-[3.4rem] lg:text-[4.1rem] font-light tracking-[0.07em] text-[#FFFDF7] uppercase"
              style={{ textShadow: "0 2px 30px rgba(231,181,77,0.18)" }}
            >
              The CCS Approach
            </motion.h1>

            {/* 3 — the three pillars: the brand statement */}
            <motion.div variants={item} className="mt-12 w-full" data-testid="gateway-pillars">
              <div className="h-px w-full max-w-2xl mx-auto bg-gradient-to-r from-transparent via-[#C28A2E]/70 to-transparent" />
              <div className="py-5 sm:py-6 flex items-center justify-center gap-3.5 sm:gap-6 flex-wrap">
                {["Communicate", "Collaborate", "Serve"].map((w, i) => (
                  <div key={w} className="flex items-center gap-3.5 sm:gap-6">
                    {i > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#E7B54D]" />}
                    <span className="font-display text-base sm:text-2xl lg:text-[1.7rem] font-normal uppercase tracking-[0.22em] sm:tracking-[0.3em] text-[#E7B54D]">
                      {w}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-px w-full max-w-2xl mx-auto bg-gradient-to-r from-transparent via-[#C28A2E]/70 to-transparent" />
            </motion.div>

            {/* 4 — the mission */}
            <motion.p
              variants={item}
              className="mt-12 font-display text-lg sm:text-2xl font-light italic text-[#F0E6D2]"
              data-testid="hero-tagline"
            >
              Empowering People. Elevating Teams. Transforming Organizations.
            </motion.p>

            {/* 5 — the movement line */}
            <motion.p
              variants={item}
              className="mt-12 max-w-2xl text-sm sm:text-[15px] leading-relaxed text-[#C9A56A] italic border-t border-b border-[#C28A2E]/20 py-5 px-2"
              data-testid="hero-movement-line"
            >
              More than a consultancy. A movement empowering people, strengthening teams and
              transforming organizations through communication, collaboration and service.
            </motion.p>

            {/* scroll indicator */}
            <motion.div variants={item} className="mt-14">
              <motion.div
                animate={{ y: [0, 7, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="text-[#E7B54D]/70"
                data-testid="hero-scroll-indicator"
              >
                <ChevronDown size={22} strokeWidth={1.5} />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ============== Choose your journey — six cards ============== */}
        <section className="relative max-w-6xl mx-auto px-6 sm:px-10 pb-8" data-testid="home-cards-section">
          <motion.div variants={item} className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#E7B54D]">Choose your journey</div>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {CARDS.map((c) => (
              <NavCard key={c.testid} c={c} />
            ))}
          </div>
        </section>

        {/* ============== Previews · testimonials · CTA · footer ============== */}
        <HomeBelowFold />
      </motion.div>
    </div>
  );
}
